/**
 * Browser-only pixel half of the artwork crop (JARVIS-391): applies a
 * projective transform to an image and hands back a JPEG blob.
 *
 * WHY WEBGL. `CanvasRenderingContext2D.setTransform` takes a 2x3 affine
 * matrix and has nowhere to put the perspective terms, so 2D canvas cannot
 * express this transform at all. The alternatives were subdividing the quad
 * into triangles and approximating each affinely (visible seams, far more
 * code) or a CPU inverse-map over `ImageData` (correct but slow on a
 * multi-megapixel scan). A fragment shader doing inverse-homography texture
 * sampling is the small, exact option, and WebGL needs no new dependency.
 *
 * The shader runs the transform BACKWARDS — for each output pixel it asks
 * which source pixel lands there — because a forward scatter leaves holes
 * wherever the warp magnifies. Sampling outside the marked quad paints
 * white, matching the flatten-onto-paper look of the renditions.
 *
 * Pure geometry lives in `perspectiveCrop.ts`; this file owns the GPU.
 */
import { getScaledDimensions } from "#utils/artworkRenditionHelpers"
import { computeHomography, type Quad } from "#utils/homography"
import { type PerspectiveCropPlan } from "#utils/perspectiveCrop"

const VERTEX_SHADER = `
attribute vec2 aPosition;
uniform vec2 uOutputSize;
varying vec2 vOutputPixel;
void main() {
  gl_Position = vec4(aPosition, 0.0, 1.0);
  vec2 unit = aPosition * 0.5 + 0.5;
  // Flip y: output row 0 is the top, clip space counts up from the bottom.
  vOutputPixel = vec2(unit.x, 1.0 - unit.y) * uOutputSize;
}
`

const FRAGMENT_SHADER = `
precision highp float;
uniform mat3 uInverse;
uniform vec2 uSourceSize;
uniform sampler2D uImage;
varying vec2 vOutputPixel;
void main() {
  vec3 mapped = uInverse * vec3(vOutputPixel, 1.0);
  vec2 source = mapped.xy / mapped.z;
  vec2 uv = source / uSourceSize;
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    gl_FragColor = vec4(1.0, 1.0, 1.0, 1.0);
    return;
  }
  gl_FragColor = texture2D(uImage, uv);
}
`

/** Two triangles covering clip space. */
const FULL_SCREEN_TRIANGLES = new Float32Array([
  -1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1,
])

export const WARP_JPEG_QUALITY = 0.85
export const WARP_CONTENT_TYPE = "image/jpeg"

const compileShader = (
  gl: WebGLRenderingContext,
  type: number,
  source: string
): WebGLShader => {
  const shader = gl.createShader(type)
  if (!shader) throw new Error("Could not create a WebGL shader for the crop")
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader)
    gl.deleteShader(shader)
    throw new Error(`Crop shader failed to compile: ${log ?? "unknown error"}`)
  }
  return shader
}

const linkProgram = (gl: WebGLRenderingContext): WebGLProgram => {
  const program = gl.createProgram()
  if (!program) throw new Error("Could not create a WebGL program for the crop")
  const vertex = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER)
  const fragment = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER)
  gl.attachShader(program, vertex)
  gl.attachShader(program, fragment)
  gl.linkProgram(program)
  // Flagged for deletion now; the driver frees them when the program goes.
  gl.deleteShader(vertex)
  gl.deleteShader(fragment)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program)
    gl.deleteProgram(program)
    throw new Error(`Crop shader failed to link: ${log ?? "unknown error"}`)
  }
  return program
}

/**
 * `uniformMatrix3fv` only accepts column-major data (WebGL 1 rejects the
 * transpose flag outright), and `computeHomography` returns row-major.
 */
const toColumnMajor = (rowMajor: number[]): Float32Array =>
  new Float32Array([
    rowMajor[0],
    rowMajor[3],
    rowMajor[6],
    rowMajor[1],
    rowMajor[4],
    rowMajor[7],
    rowMajor[2],
    rowMajor[5],
    rowMajor[8],
  ])

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  quality: number = WARP_JPEG_QUALITY
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error("Canvas produced no JPEG data for the crop"))
        }
      },
      WARP_CONTENT_TYPE,
      quality
    )
  })

/**
 * Downscales a warped bitmap to `maxEdge` (never upscaling) and encodes it.
 * Plain 2D canvas: the perspective is already baked in by this point, so
 * what is left is an affine shrink.
 */
export const encodeBitmapToJpeg = async (
  bitmap: ImageBitmap,
  maxEdge: number,
  quality: number
): Promise<Blob> => {
  const { width, height } = getScaledDimensions(
    bitmap.width,
    bitmap.height,
    maxEdge
  )
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext("2d")
  if (!context) {
    throw new Error("2D canvas context unavailable to encode the crop")
  }
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = "high"
  // The warp paints white outside the quad, but a resample at the edges can
  // still pull alpha through; JPEG has none, so flatten on white to match.
  context.fillStyle = "#ffffff"
  context.fillRect(0, 0, width, height)
  context.drawImage(bitmap, 0, 0, width, height)
  return canvasToBlob(canvas, quality)
}

/**
 * Source images can exceed the GPU's max texture size (as low as 4096 on
 * older hardware), where `texImage2D` fails outright. Pre-shrinking the
 * bitmap and scaling the source quad by the same factor keeps the warp
 * exact in output space; the lost detail was beyond the export cap anyway.
 */
const fitSourceToTextureLimit = (
  gl: WebGLRenderingContext,
  bitmap: ImageBitmap,
  sourceQuad: Quad
): { source: TexImageSource; width: number; height: number; quad: Quad } => {
  const limit = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number
  const longest = Math.max(bitmap.width, bitmap.height)
  if (!limit || longest <= limit) {
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      quad: sourceQuad,
    }
  }

  const scale = limit / longest
  const width = Math.max(1, Math.floor(bitmap.width * scale))
  const height = Math.max(1, Math.floor(bitmap.height * scale))
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext("2d")
  if (!context) {
    throw new Error("2D canvas context unavailable to shrink the crop source")
  }
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = "high"
  context.drawImage(bitmap, 0, 0, width, height)
  return {
    source: canvas,
    width,
    height,
    quad: sourceQuad.map((point) => ({
      x: point.x * scale,
      y: point.y * scale,
    })) as unknown as Quad,
  }
}

/**
 * Renders `plan` over `bitmap` and resolves to the warped JPEG.
 *
 * Throws rather than degrading: a crop that silently came out wrong would be
 * written over the piece every surface shows, so the editor surfaces the
 * failure and keeps the original.
 */
export const warpImageToBlob = async (
  bitmap: ImageBitmap,
  plan: PerspectiveCropPlan
): Promise<Blob> => {
  const canvas = document.createElement("canvas")
  canvas.width = plan.width
  canvas.height = plan.height

  // `preserveDrawingBuffer` so `toBlob` still sees the pixels: without it the
  // buffer is free to be cleared the moment the draw call returns.
  const gl = (canvas.getContext("webgl", {
    preserveDrawingBuffer: true,
    premultipliedAlpha: false,
  }) ?? null) as WebGLRenderingContext | null
  if (!gl) {
    throw new Error("WebGL is unavailable, so this image cannot be cropped")
  }

  const program = linkProgram(gl)
  const buffer = gl.createBuffer()
  const texture = gl.createTexture()

  try {
    const fitted = fitSourceToTextureLimit(gl, bitmap, plan.sourceQuad)

    // The shader maps output pixels back to source pixels, so it needs the
    // plan's matrix inverted. Solving the correspondence the other way round
    // is exact, where inverting the 3x3 numerically would not be.
    const destinationQuad: Quad = [
      { x: 0, y: 0 },
      { x: plan.width, y: 0 },
      { x: plan.width, y: plan.height },
      { x: 0, y: plan.height },
    ]
    const inverse = computeHomography(destinationQuad, fitted.quad)
    if (!inverse) {
      throw new Error("Those corners do not form a usable shape")
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(gl.ARRAY_BUFFER, FULL_SCREEN_TRIANGLES, gl.STATIC_DRAW)

    gl.bindTexture(gl.TEXTURE_2D, texture)
    // No mipmaps and CLAMP_TO_EDGE: scans are almost never power-of-two, and
    // WebGL 1 renders NPOT textures black under any other wrap or filter.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    // No UNPACK_FLIP_Y: uploaded unflipped, texture v runs top-down, which is
    // the same direction as the image pixel coordinates the plan is built in.
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      fitted.source
    )

    gl.useProgram(program)
    const position = gl.getAttribLocation(program, "aPosition")
    gl.enableVertexAttribArray(position)
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)
    gl.uniform2f(
      gl.getUniformLocation(program, "uOutputSize"),
      plan.width,
      plan.height
    )
    gl.uniform2f(
      gl.getUniformLocation(program, "uSourceSize"),
      fitted.width,
      fitted.height
    )
    gl.uniformMatrix3fv(
      gl.getUniformLocation(program, "uInverse"),
      false,
      toColumnMajor(inverse)
    )
    gl.uniform1i(gl.getUniformLocation(program, "uImage"), 0)

    gl.viewport(0, 0, plan.width, plan.height)
    gl.drawArrays(gl.TRIANGLES, 0, 6)

    return await canvasToBlob(canvas)
  } finally {
    gl.deleteTexture(texture)
    gl.deleteBuffer(buffer)
    gl.deleteProgram(program)
    // Frees the drawing buffer immediately: browsers cap live WebGL contexts
    // (~16) and drop the oldest, so a few crops in a row could otherwise kill
    // a context still being read.
    gl.getExtension("WEBGL_lose_context")?.loseContext()
  }
}
